<template>
  <div class="preview-wrapper" tabindex="0" @keydown="onKeyDown">
    <div class="preview-header">
      <button class="close-btn" @click="onCloseClick" />
      <div :class="currentFileIcon" />
      <div class="preview-title"> {{ filename }}</div>
    </div>
    <div class="preview-content" >
      <FileTable v-show="isPreviewingFolder" ref="fileTableInst" @update="onUpdate" />
      <div class="viewer-wrapper">
        <div class="uv-container">
          <UniversalViewerWrapper :style="{ width: uvWrapperWidth }" class="uv-wrapper"
          ref="uvWrapperInst"
          @prev="onPrev" @next="onNext" @hide="closePreview" @leaveArchive="onLeaveArchive" @viewFile="onViewFile"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed } from 'vue'
  import UniversalViewerWrapper from '@/components/UniversalViewerWrapper.vue'
  import { extension, getIconClass } from '@/utils/get-icon'
  import FileTable from '@/components/FileTable.vue'

  const uvWrapperInst = ref(null)
  const navIndex = ref(-1)
  const uvItems = ref([])
  const fileTableInst = ref(null)
  const isPreviewingFolder = ref(false)

  const isReady = computed(() => {
    return navIndex.value !== -1 && uvItems.value.length > 0
  })

  const uvWrapperWidth = computed(() => {
    return isPreviewingFolder.value ? 'calc(100% - 536px)' : '100%'
  })

  const currentFileIcon = computed(() => {
    const cls = 'preview-title-icon'
    return cls + ' ' + icon.value
  })

  const uvFileItems = computed(() => {
    return uvItems.value.filter((fileObj) => {
      return fileObj.type === 'file'
    }).map((fileObj) => {
      return {
        id: fileObj.id,
        size: fileObj.size,
        name: fileObj.file?.name || fileObj.name,
        requestItem: fileObj.requestItem || fileObj.file,
        requestDownload: false,
        requestPrint: false,
      }
    })
  })

  const uvFolderItemsLength = computed(() => {
    return uvItems.value.filter((fileObj) => {
      return fileObj.type === 'folder'
    }).length
  })

  const currentViewedFile = computed(() => {
    if (!isReady.value) {
      return null
    }

    return uvItems.value[navIndex.value]
  })

  const filename = computed(() => {
    return currentViewedFile.value?.name
  })

  const icon = computed(() => {
    if (currentViewedFile.value?.type === 'folder') {
      return 'folder'
    }

    if (!filename.value) {
      return ''
    }

    return getIconClass(extension(filename.value))
  })

  const emits = defineEmits(['close_preview'])

  const onPrev = () => {
    uvWrapperInst.value.showPrev()
  }

  const onNext = () => {
    uvWrapperInst.value.showNext()
  }

  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      uvWrapperInst.value.showPrev()
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      uvWrapperInst.value.showNext()
    } else if (e.key === 'Escape') {
      uvWrapperInst.value.close()
    }
  }

  const onViewFile = (index) => {
    navIndex.value = uvFolderItemsLength.value + index

    if (isPreviewingFolder.value) {
      fileTableInst.value.selectIndex(navIndex.value)
    }
  }

  const onUpdate = (index) => {
    navIndex.value = index
    uvItems.value = fileTableInst.value.getChildren()
    
    preview()
  }

  const preview = () => {
    if (!isReady.value) {
      return
    }

    if (uvFileItems.value.length === 0) {
      return
    }

    const fileIndex = navIndex.value - uvFolderItemsLength.value
    
    uvWrapperInst.value.show(fileIndex, uvFileItems.value)
  }

  const onLeaveArchive = () => {
    uvWrapperInst.value.leaveArchive()
  }

  const closePreview = () => {
    emits('close_preview')
  }

  const onCloseClick = () => {
    uvWrapperInst.value.close()
  }

  const createRootNode = (fileObjs) => {
    const targetFiles = [...fileObjs.fileInfos]

    if (targetFiles.length === 0) {
      return null
    }

    // get root folder name
    const rootFolderName = targetFiles[0].relativePath.split('/')[0]
    const rootNode = { type: 'folder', name: rootFolderName, children: [] }

    targetFiles.forEach(fileInfo => {
        const pathParts = fileInfo.relativePath.split('/')
        let currentNode = rootNode

        pathParts.slice(1).forEach((part, index) => {
            // file
            if (index === pathParts.length - 2) {
              currentNode.children.push({ type: 'file', name: part, file: fileInfo.content })
              return
            }

            // folder
            let folderNode = currentNode.children.find(child => child.type === 'folder' && child.name === part)

            if (!folderNode) {
                folderNode = { type: 'folder', name: part, children: [] }
                currentNode.children.push(folderNode)
            }

            currentNode = folderNode
        })
    })

    const sortTree = (node) => {
      if (!node.children || node.children.length === 0) {
        return
      }

      node.children.sort((a, b) => {
        if (a.type === b.type) {
          return a.name.localeCompare(b.name)
        }

        return a.type === 'folder' ? -1 : 1
      })

      node.children.forEach(child => {
        if (child.type === 'folder') {
          sortTree(child)
        }
      })
    }

    sortTree(rootNode)

    return rootNode
  }

  const init = async (index, items) => {
    // preview folder
    if (items[index].type === 'folder') {
      isPreviewingFolder.value = true
      let rootNode = items[index]

      if (items[index].fileInfos) {
        rootNode = createRootNode(items[index])
      }

      await fileTableInst.value.setRootFolder(rootNode)
      return
    }

    // preview file
    isPreviewingFolder.value = false
    const fileItems = items.filter((fileObj) => {
      return fileObj.type === 'file'
    })

    navIndex.value = fileItems.findIndex((fileObj) => {
      return fileObj.id === items[index].id
    })
    uvItems.value = fileItems
    preview()
  }

  defineExpose({
    init,
  })
</script>

<style lang="scss" scoped>
@import '@/mixins/style.scss';

.folder {
  background-image: url('@/assets/filetype/folder.png');
  background-size: 100%;
}

.preview-wrapper {
  position: fixed;
  width: 100%;
  height: 100%;
  background-color: rgba(16, 18, 20, .9);
  z-index: 10;
  display: flex;
  flex-direction: column;
  -webkit-user-select: none;
  user-select: none;
  .preview-header {
    height: 60px;
    width: 100%;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.70) 0%, rgba(0, 0, 0, 0.00) 100%);
    display: flex;
    align-items: center;
    .close-btn {
      width: 24px;
      height: 24px;
      flex-shrink: 0;
      border: 0;
      margin: 18px 20px;
      cursor: pointer;
      background-color: transparent;
      background-image: url('@/assets/icn_close.svg');
      opacity: 0.7;
      &:hover {
        opacity: 0.9;
      }
      &:active {
        opacity: 1;
      }
    }

    .preview-title-icon {
      width: 24px;
      height: 24px;
      margin-right: 8px;
    }

    .preview-title {
      color: #FFF;
      font-size: 15px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px;
    }
  }

  .preview-content {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    .viewer-wrapper {
      flex-grow: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      .uv-container {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        .uv-wrapper {
          height: calc(100% - 60px);
        }
      }
    }
  }
}

</style>